import { useDispatch, useSelector } from "react-redux";
import { Grid, Input, Slider, Typography, Select, MenuItem, FormControl, IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { selectOptions, setNormalization } from "../reducers/optionsSlice";
import { updateMultiSegmentForecastThunk, updateWell } from "../reducers/wellSlice";
import { updateTypeCurveForecast } from "../reducers/typeCurveSlice";
import { updateGroupWellForecast } from "../reducers/groupWellSlice";

import { convertVolume, revertVolume } from '../helpers/convertVolume';
import { Lock, LockOpen } from "@mui/icons-material";

const MONTH_LENGTH = 30.436875;

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    margin: '10px 0',
    padding: '0 5px',
    backgroundColor: 'transparent',
  },
  input: {
    width: 70,
  },
}));

export const ForecastSetting = ({ forecastListTab, well, parameter, typeCurve, groupWell, darkMode }) => {

  const classes = useStyles();

  const dispatch = useDispatch();

  const options = useSelector(selectOptions);
  
  const handleInputChange = (val, parameter) => {
    try {
      let value = Number(val || 0);

      if (parameter === 'di' || parameter === 'dlim' || parameter === 'd2lim' || parameter === 'd2i' || parameter === 'd3i') {
        value = value / 100;
      } else if (parameter === 'cutoff') {
        value = revertVolume(value, options.units, options.fluid) * MONTH_LENGTH;
      } else if (parameter === 'q1i') {
        value = revertVolume(value, options.units, options.fluid) * MONTH_LENGTH;
      };
      if (parameter === 'cutoff') {
        if (forecastListTab === 0) {
          dispatch(updateMultiSegmentForecastThunk({well: {...well, params: {...well.params, cutoff: value}}, param: parameter, value: value, fluid: options.fluid}));
          // dispatch(updateCutoffThunk({well: well, fluid: options.fluid, cutoff: value}));
        } else if (forecastListTab === 1) {
          dispatch(updateTypeCurveForecast({well: well, param: 'cutoff', value: value, fluid: options.fluid}));
        } else if (forecastListTab === 2) {
          dispatch(updateGroupWellForecast({well: well, param: 'cutoff', value: value, fluid: options.fluid}));
        };
      } else {
        if (forecastListTab === 0) {
          let newParams = {...well.params};
          let newLockedParams = newParams[options.fluid]?.lockedParams ? [...newParams[options.fluid]?.lockedParams] : [];
          
          
          if ((parameter === 'ti' || parameter === 'q1i' || parameter === 'di' || parameter === 'b1') && newLockedParams?.find(p => p.param === parameter)) {
            console.log('Updating param lock');
            newLockedParams = [...newLockedParams.filter(p => p.param !== parameter), {param: parameter, value}];
            newParams = {...newParams, [options.fluid]: {...newParams[options.fluid], lockedParams: newLockedParams}};
          };

          dispatch(updateMultiSegmentForecastThunk({well: {...well, params: newParams}, param: parameter, value: value, fluid: options.fluid}));

        } else if (forecastListTab === 1) {
          dispatch(updateTypeCurveForecast({well: well, param: parameter, value: value, fluid: options.fluid}));
        } else if (forecastListTab === 2) {
          dispatch(updateGroupWellForecast({well: well, param: parameter, value: value, fluid: options.fluid}));
        };
      };


    } catch (error) {
      console.log('Could not update well parameter');
      console.log(error);
      return;
    };
  };

  const value = getValue(well.params, options.fluid, parameter, options) || 0;
  const lockedParams = well?.params?.[options?.fluid]?.lockedParams;

  return (
    <div className={classes.root}>
      <Typography id='input-slider' gutterBottom noWrap style={{marginLeft: 5}}>
        {getLabelName(parameter)} <span style={{fontSize: 12}}>{getUnits(parameter, options.fluid, options.units)}</span>
      </Typography>
      {
      parameter === "normalization" ?
        ((typeCurve?.details && forecastListTab !== 1) ? 
        <FormControl variant="filled" style={{width: '100%'}}>
          <Select 
            value={options.normalization}
            label="Normalization"
            onChange={(e) => handleNormalization(e)}
            style={{width: '100%'}}
          >
            <MenuItem value="off">Off</MenuItem>
            <MenuItem value="length" disabled={!typeCurve?.details?.length > 0}>Completed Length</MenuItem>
            <MenuItem value="spacing" disabled={!typeCurve?.details?.spacing > 0}>Cluster Spacing</MenuItem>
            <MenuItem value="tpm" disabled={!typeCurve?.details?.tpm > 0}>TPM</MenuItem>
          </Select>
        </FormControl> : <Typography color="textSecondary">Please select a type curve to enable normalization</Typography>)
      :
        <Grid container spacing={0} alignItems="flex-start" style={{marginLeft: 0}}>
          {forecastListTab === 0 && (parameter === 'ti' || parameter === 'q1i' || parameter === 'di' || parameter === 'b1') && 
          <Tooltip title="Lock Parameter">
            <IconButton size="small" color={(darkMode ? "inherit" : "primary")} style={{marginRight: 0}} onClick={e => handleLockParameter(parameter, value, well, options?.fluid)}>
              {!lockedParams?.find(p => p.param === parameter) ? <LockOpen fontSize="12px" /> : <Lock fontSize="12px"/>}
            </IconButton>
          </Tooltip>}
          <Grid item xs>
            <Slider
              value={formatValue(parameter, value, options)}
              onChange={(e, newValue) => handleInputChange(newValue, parameter)}
              min={getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[0]}
              max={getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[1]}
              step={getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[2]}
              valueLabelDisplay="auto"
              style={{color: (darkMode ? '#fff' : '#0758B7'), marginLeft: 10}}
            />
          </Grid>
          <Grid item>
            <Input
              className={classes.input}
              value={formatValue(parameter, value, options)}
              margin="dense"
              onChange={(e) => handleInputChange(e.target.value, parameter)}
              variant="filled"
              inputProps={{
                min: getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[0],
                max: getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[1],
                step: getMinMaxStep(parameter, value, options.units, options.fluid, well)?.[2],
                type: 'number',
              }}
              style={{marginLeft: 25, marginRight: 10}}
            />
          </Grid>
        </Grid>
      }
    </div>
  );

  // FUNCTIONS
  function handleNormalization(e) {
    e.target.value && typeCurve?.details && dispatch(setNormalization(e.target.value)); // only update normalization if type curve is selected
    if (!typeCurve?.details) console.log('Please select a type curve first!');
  };
  
  function handleLockParameter(parameter, value, well, fluid) {
    try {
      let newParams = {...well.params};
      let newLockedParams = newParams[fluid]?.lockedParams ? [...newParams[fluid]?.lockedParams] : [];

      if (!newLockedParams || newLockedParams?.length === 0) {
        newLockedParams = [{param: parameter, value}];  
      } else if (!newLockedParams?.find(p => p.param === parameter)) {
        console.log('adding new param')
        newLockedParams = [...newLockedParams, {param: parameter, value}];  
      } else {
        newLockedParams = newLockedParams.filter(p => p.param !== parameter);
      };

      newParams = {...newParams, [fluid]: {...newParams[fluid], lockedParams: newLockedParams}};

      dispatch(updateWell({uwi: well.uwi, params: newParams}));
  
    } catch (error) {
      console.log(error);
    };
  };
};


function getValue(params, fluid, param, options) {
  switch(param) {
    case 'cutoff':
      return convertVolume(params?.cutoff, options.units, options.fluid);
    // case 'q1i':
    //   return convertVolume(params?.q1i, options.units, options.fluid);
    default:
      return params?.[fluid]?.[param];
  };
};

function getMaxMonthsOnline(well, fluid) {
  try {
    return well?.production?.byFluid?.[fluid]?.slice(-1)?.[0]?.monthsOnline;
  } catch (error) {
    console.log(error);
    return 100;
  };
};

function getMinMaxStep(param, value, units, fluid, well) {
  switch(param) {
    case 'di':
      return [0, 100, 0.01];
    case 'dlim':
      return [0, 100, 0.01];
    case 'd2lim':
      return [0, 100, 0.01];
    case 'd2i':
      return [0, 100, 0.01];
    case 'd3i':
      return [0, 100, 0.01];
    case 'b1':
      return [0, 2, 0.01];
    case 'b2':
      return [0, 2, 0.01];
    case 'b3':
      return [0, 2, 0.01];
    case 'cutoff':
      return [0.1, units === 'metric' ? 100 : 1000, 0.1];
    case 'q1i':
      return [0.1, units === 'metric' ? 1000 : 10000, 0.1];
    case 'ti':
      return [1, getMaxMonthsOnline(well, fluid), 1];
    case 't2':
      return [1, 1000, 1];
    case 't3':
      return [1, 1000, 1];
    default:
      return [Math.pow(10, Math.floor(Math.log10(value))), Math.pow(10, Math.ceil(Math.log10(value))), 0.1];
  };
};

function formatValue(param, value, options) {
  // const numValue = !isNaN(value) ? value : 0;
  switch(param) {
    case 'di':
      return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
    case 'dlim':
      return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
    case 'd2lim':
      return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
    case 'd2i':
      return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
    case 'd3i':
      return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
    case 'cutoff':
      return Math.round((value / MONTH_LENGTH + Number.EPSILON) * 100) / 100;
      // return Math.round((convertVolume(value, options.units, options.fluid) / MONTH_LENGTH + Number.EPSILON) * 100) / 100;
    case 'q1i':
      return (Math.round((convertVolume(value, options.units, options.fluid) / MONTH_LENGTH + Number.EPSILON) * 100) / 100);
    default:
      return Math.round((value + Number.EPSILON) * 100) / 100;
  }
};

function getLabelName(parameter) {
  switch(parameter) {
    case 'cutoff':
      return 'Forecast Cutoff';
    case 'q1i':
      return 'Initial Production Rate';
    case 'di':
      return 'Initial Decline Rate';
    case 'dlim':
      return 'First Transition Rate';
    case 'd2lim':
      return 'Second Transition Rate';
    case 'd2i':
      return 'Second Initial Rate';
    case 'd3i':
      return 'Third Initial Rate';
    case 'b1':
      return 'First Hyperbolic Constant';
    case 'b2':
      return 'Second Hyperbolic Constant';
    case 'b3':
      return 'Third Hyperbolic Constant';
    case 'ti':
      return 'Initial Month';
    case 't2':
      return 'Second Segment Month';
    case 't3':
      return 'Third Segment Month';
    case 'normalization':
      return 'Normalization';
    case 'uncertainty':
      return 'Model Uncertainty';
    default:
      return '';
  };
};

function getUnits(parameter, fluid, units) {
  switch(parameter) {
    case 'cutoff':
      return units === 'metric' ? ((fluid === 'oil' || fluid === 'condensate' || fluid === 'water') ? 'm3/day' : 'E3m3/day') : ((fluid === 'oil' || fluid === 'condensate' || fluid === 'water') ? 'bbl/day' : 'mscf/day');
    case 'q1i':
      return units === 'metric' ? ((fluid === 'oil' || fluid === 'condensate' || fluid === 'water') ? 'm3/day' : 'E3m3/day') : ((fluid === 'oil' || fluid === 'condensate' || fluid === 'water') ? 'bbl/day' : 'mscf/day');
    case 'di':
      return '%/month';
    case 'dlim':
      return '%/month';
    case 'd2lim':
      return '%/month';
    case 'd2i':
      return '%/month';
    case 'd3i':
      return '%/month';
    default:
      return '';
  };
};


